import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environment";
import { Observable } from "rxjs";
import { Hierarchy } from "../components/hierarchy/hierarchy.interface";
import { DropdownItemWithDate } from "../interfaces/dropdown-item.interface";
import { Facility, FacilityListItem } from "../interfaces/facility.interface";
import { ListResponse } from "../interfaces/list-response.interface";
import { sanitizeParams } from "../sanitize-params";
import { FacilityFeeSchedule} from "../interfaces/facility-fee-schedule.interface";
import { ProductivityBenchmark } from "../interfaces/Productivity.interface";

@Injectable({ providedIn: "root" })
export class FacilitiesClient {
  constructor(private httpClient: HttpClient) {}
  endpoint = "facility";

  getFacilities(filters: {
    searchBy?: string;
    keyword?: string;
    includeDisabled?: boolean;
    includeDeleted?: boolean;
    page?: number;
    length?: number;
    sortBy?: string;
    sortDir?: string;
    corpId?: string;
    /** Only the facilities the current user is assigned to (ignored for super admins). */
    assignedOnly?: boolean;
  }): Observable<ListResponse<FacilityListItem>> {
    const params = sanitizeParams(filters) as Record<string, any>;

    return this.httpClient.get<ListResponse<FacilityListItem>>(
      `${environment.endpoint}/${this.endpoint}/list`,
      { params }
    );
  }

  getFacilitiesDropdown(corpId: string): Observable<DropdownItemWithDate[]> {
    return this.httpClient.get<DropdownItemWithDate[]>(
      `${environment.endpoint}/${this.endpoint}/dropdown/${corpId}`
    );
  }

  getFacility(facilityId: string): Observable<Facility> {
    return this.httpClient.get<Facility>(
      `${environment.endpoint}/${this.endpoint}/get/${facilityId}`
    );
  }

  deleteFacility(facilityid: string) {
    return this.httpClient.delete(
      `${environment.endpoint}/${this.endpoint}/delete/${facilityid}`
    );
  }

  enableFacility(facilityId: string) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/enabled/${facilityId}`,
      null
    );
  }

  addFacility(facility: Facility) {
    return this.httpClient.post<Facility>(
      `${environment.endpoint}/${this.endpoint}/add`,
      facility
    );
  }

  updateFacility(facility: Facility) {
    return this.httpClient.put<Facility>(
      `${environment.endpoint}/${this.endpoint}/edit`,
      facility
    );
  }

  disableFacility(facilityId: string) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/disabled/${facilityId}`,
      null
    );
  }

  getAllFacilitiesHierarchy(
    corpId: string,
    searchBy: string,
    searchText?: string
  ): Observable<Hierarchy> {
    const params = { corpid: corpId } as any;
    if (searchBy && searchText) {
      params.searchBy = searchBy;
      params.keyword = searchText;
    }
    return this.httpClient.get<Hierarchy>(
      `${environment.endpoint}/${this.endpoint}/facility-hierarchy-list`,
      {
        params: params,
      }
    );
  }

  moveFacilityToLevel(
    facilityIds: string[],
    levelId: number,
    corpId: string
  ): Observable<void> {
    return this.httpClient.put<void>(
      `${environment.endpoint}/${this.endpoint}/move`,
      { levelid: levelId, facilitiyids: facilityIds, corpid: corpId }
    );
  }

  unassignFacility(facilityId: string): Observable<void> {
    return this.httpClient.put<void>(
      `${environment.endpoint}/${this.endpoint}/remove-level/${facilityId}`,
      {}
    );
  }
  getFeeSchedule(facilityId: string) {
    return this.httpClient.get<FacilityFeeSchedule[]>(
      `${environment.endpoint}/${this.endpoint}/fee-schedule/${facilityId}`
    );
  }
  deleteFeeSchedule(feeScheduleDetails: FacilityFeeSchedule[]) {
    return this.httpClient.post(
      `${environment.endpoint}/${this.endpoint}/fee-schedule/delete`, feeScheduleDetails    
    );
  }
  
  getProductivity(facilityId: string) {
    return this.httpClient.get<ProductivityBenchmark[]>(
      `${environment.endpoint}/${this.endpoint}/get-productivitybenchmarks/${facilityId}`
    );
  }
}
